/**
 * Chase Utils - Shared utilities for chase-based order execution
 *
 * Consolidates common patterns from:
 * - evflow-executor.js
 * - pair-trade-executor.js
 * - chase-tracker.js
 *
 * Centralizes:
 * - Position verification with fresh API calls
 * - ATR fallback (2%) when service unavailable
 * - Order existence verification
 *
 * Note: waitForOrderConfirmation was removed - delta tracking in ChaseTracker
 * now handles fill detection via PositionDeltaTracker (500ms polling)
 */

class ChaseUtils {
    constructor() {
        this.DEFAULT_POLL_INTERVAL_MS = 500;
        this.ATR_FALLBACK_PERCENT = 0.02; // 2% fallback when ATR unavailable
    }

    /**
     * Get current price for a symbol
     * Tries BrowserPriceService first (WebSocket), then BrowserHyperliquidClient
     * Handles HIP-3 symbols that aren't in allMids
     *
     * @param {string} symbol - Asset symbol (e.g., "ETH", "xyz:TSLA")
     * @returns {Promise<number|null>} Price or null if unavailable
     */
    async getPrice(symbol) {
        // Try WebSocket price service first (fastest)
        if (window.BrowserPriceService?.isReady?.()) {
            const price = window.BrowserPriceService.getPrice?.(symbol);
            if (price) return parseFloat(price);
        }

        // Fallback to Hyperliquid client
        if (window.BrowserHyperliquidClient) {
            const mids = await window.BrowserHyperliquidClient.getAllMids();
            if (mids?.[symbol]) return parseFloat(mids[symbol]);

            // HIP-3 symbols not in allMids - get from orderbook
            if (symbol.includes(':')) {
                try {
                    const price = await window.BrowserHyperliquidClient.getCurrentMarketPrice(symbol);
                    if (price) return parseFloat(price);
                } catch (err) {
                    console.error(`[CHASE-UTILS] Failed to get HIP-3 price for ${symbol}:`, err);
                }
            }
        }
        return null;
    }

    /**
     * Verify order exists using FRESH API call (not cached tracker data)
     *
     * FIX: Uses direct API instead of BrowserPositionTracker cache
     * Bug fixed: Cache could be stale, showing order that was already filled
     *
     * @param {string} symbol - Asset symbol
     * @param {string} side - 'B' or 'A' (API format)
     * @param {number} size - Order size
     * @param {number} retries - Number of retry attempts
     * @param {number} delayMs - Delay between retries
     * @returns {Promise<Object|null>} Order object or null
     */
    async verifyOrderExists(symbol, side, size, retries = 3, delayMs = 500) {
        // Validate size to prevent division by zero
        if (!size || size <= 0) {
            console.warn(`[CHASE-UTILS] Invalid size (${size}), cannot verify order`);
            return null;
        }

        const isHip3 = symbol.includes(':');

        for (let attempt = 1; attempt <= retries; attempt++) {
            if (attempt > 1) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }

            try {
                let freshOrders;
                if (isHip3) {
                    if (!window.BrowserHyperliquidClient?.infoClient || !window.BrowserHyperliquidClient?.publicAddress) {
                        console.warn('[CHASE-UTILS] infoClient or publicAddress not available for HIP-3 verification');
                        return null;
                    }
                    const dexName = symbol.split(':')[0];
                    freshOrders = await window.BrowserHyperliquidClient.infoClient.openOrders({
                        user: window.BrowserHyperliquidClient.publicAddress,
                        dex: dexName
                    });
                } else {
                    freshOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                }

                // Match by asset + side + size (1% tolerance OR partial fill)
                for (const o of freshOrders) {
                    if (o.coin === symbol && o.side === side) {
                        const orderSize = parseFloat(o.sz);
                        const sizeMatch = Math.abs(orderSize - size) / size < 0.01;
                        const isPartialFill = orderSize > 0 && orderSize < size * 0.99;

                        if (sizeMatch || isPartialFill) {
                            if (isPartialFill) {
                                console.log(`[CHASE-UTILS] Order verified (partial fill, attempt ${attempt}): ${symbol} size ${size} → ${orderSize}`);
                            } else {
                                console.log(`[CHASE-UTILS] Order verified (attempt ${attempt}): ${symbol} oid=${o.oid}`);
                            }
                            return o;
                        }
                    }
                }
            } catch (error) {
                console.warn(`[CHASE-UTILS] Order verification error (attempt ${attempt}):`, error.message);
            }
        }

        console.warn(`[CHASE-UTILS] Order not found after ${retries} attempts: ${symbol} ${side}`);
        return null;
    }

    /**
     * Verify position exists using FRESH API call
     *
     * FIX: Fetches fresh position data to avoid stale cache issues
     * Bug fixed: Cache showed no position when position actually existed
     *
     * @param {string} symbol - Asset symbol
     * @param {string} direction - 'LONG' or 'SHORT'
     * @returns {Promise<boolean>} True if position exists and matches direction
     */
    async verifyPositionExists(symbol, direction) {
        try {
            const isHip3 = symbol.includes(':');
            let positions;

            if (isHip3) {
                const dexName = symbol.split(':')[0];
                const userState = await window.BrowserHyperliquidClient.getUserStateForDex(dexName);
                positions = userState?.assetPositions || [];
            } else {
                positions = await window.BrowserHyperliquidClient.getUserPositions();
            }

            const pos = positions.find(p => p.position?.coin === symbol);
            if (!pos?.position) {
                console.log(`[CHASE-UTILS] No position found for ${symbol}`);
                return false;
            }

            const posSize = parseFloat(pos.position.szi || 0);
            const posDirection = posSize > 0 ? 'LONG' : 'SHORT';

            if (Math.abs(posSize) > 0 && posDirection === direction) {
                console.log(`[CHASE-UTILS] Position verified: ${symbol} ${posDirection} size=${posSize}`);
                return true;
            }

            console.log(`[CHASE-UTILS] Position mismatch: expected ${direction}, got ${posDirection} with size ${posSize}`);
            return false;
        } catch (error) {
            console.error(`[CHASE-UTILS] Error verifying position:`, error);
            return false;
        }
    }

    /**
     * Get position entry price from fresh API
     *
     * @param {string} symbol - Asset symbol
     * @param {number} maxAttempts - Max polling attempts
     * @param {number} pollInterval - Interval between polls in ms
     * @returns {Promise<{entryPrice: number, size: number}|null>}
     */
    async getPositionEntry(symbol, maxAttempts = 10, pollInterval = 500) {
        const isHip3 = symbol.includes(':');

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                let positions;
                if (isHip3) {
                    const dexName = symbol.split(':')[0];
                    const userState = await window.BrowserHyperliquidClient.getUserStateForDex(dexName);
                    positions = userState?.assetPositions || [];
                } else {
                    positions = await window.BrowserHyperliquidClient.getUserPositions();
                }

                const pos = positions.find(p => p.position?.coin === symbol);
                if (pos?.position?.entryPx) {
                    return {
                        entryPrice: parseFloat(pos.position.entryPx),
                        size: Math.abs(parseFloat(pos.position.szi || 0))
                    };
                }

                if (attempt < maxAttempts) {
                    await new Promise(r => setTimeout(r, pollInterval));
                }
            } catch (e) {
                if (attempt < maxAttempts) {
                    await new Promise(r => setTimeout(r, pollInterval));
                }
            }
        }

        console.error(`[CHASE-UTILS] Position not found for ${symbol} after ${maxAttempts} attempts`);
        return null;
    }

    /**
     * Place a limit order with offset from market price
     *
     * @param {string} symbol - Asset symbol
     * @param {string} direction - 'LONG' or 'SHORT'
     * @param {number} size - Order size
     * @param {number} price - Current market price
     * @param {number} percentOffset - Offset from market (default 0.5%)
     * @returns {Promise<{success: boolean, orderId?: string, limitPrice?: number, error?: string}>}
     */
    async placeLimitOrder(symbol, direction, size, price, percentOffset = 0.005) {
        if (!window.BrowserHyperliquidClient?.isInitialized) {
            return { success: false, error: 'BrowserHyperliquidClient not available' };
        }

        try {
            const orderSide = direction === 'LONG' ? 'buy' : 'sell';
            const priceNum = parseFloat(price);
            const limitPrice = orderSide === 'buy'
                ? priceNum * (1 - percentOffset)
                : priceNum * (1 + percentOffset);

            console.log(`[CHASE-UTILS] Placing ${symbol} ${orderSide}: size=${size}, price=${limitPrice.toFixed(6)}`);

            const result = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset: symbol,
                orderSide: orderSide,
                size: size,
                price: limitPrice,
                orderType: 'limit',
                reduceOnly: false,
                timeInForce: 'GTC'
            });

            if (!result?.success) {
                return { success: false, error: result?.error || 'SDK call failed' };
            }

            const status = result?.result?.response?.data?.statuses?.[0];
            if (status?.error) {
                return { success: false, error: status.error };
            }

            const orderId = this.extractOrderId(result);
            if (!orderId) {
                return { success: false, error: 'No order ID in response' };
            }

            console.log(`[CHASE-UTILS] Order placed: oid=${orderId}`);
            return {
                success: true,
                orderId: String(orderId),
                limitPrice: limitPrice,
                result
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    /**
     * Extract order ID from SDK response
     * Handles multiple possible response formats
     *
     * @param {Object} result - SDK response
     * @returns {string|null} Order ID or null
     */
    extractOrderId(result) {
        const status = result?.result?.response?.data?.statuses?.[0];
        return status?.resting?.oid ||
               status?.filled?.oid ||
               result?.cloid ||
               result?.orderId ||
               result?.oid ||
               null;
    }

    /**
     * Start chase for an order
     *
     * @param {string} orderId - Order ID
     * @param {string} symbol - Asset symbol
     * @param {string} direction - 'LONG' or 'SHORT'
     * @param {number} size - Order size
     * @param {number} price - Limit price
     * @param {Object} settings - Chase settings
     * @returns {Promise<void>}
     */
    async startChase(orderId, symbol, direction, size, price, settings = {}) {
        if (!window.BrowserChaseTracker) {
            console.warn('[CHASE-UTILS] BrowserChaseTracker not available');
            return;
        }

        const defaultSettings = {
            tickDistance: 1,
            frequencyRangeMin: 5,
            frequencyRangeMax: 5,
            useAnchor: false,
            tpslEnabled: false
        };

        const chaseSettings = { ...defaultSettings, ...settings };

        const orderData = {
            coin: symbol,
            asset: symbol,
            side: direction === 'LONG' ? 'B' : 'A',
            sz: String(size),
            size: size,
            limitPx: String(price),
            price: price,
            oid: orderId
        };

        await window.BrowserChaseTracker.startChase(orderId, orderData, chaseSettings);
        console.log(`[CHASE-UTILS] Chase started for ${symbol}: oid=${orderId}`);
    }

    /**
     * Get ATR with fallback
     *
     * FIX: Returns 2% of entry price when ATR service unavailable
     * Bug fixed: No SL/TP placed when candleSnapshot API returned 500
     *
     * @param {string} symbol - Asset symbol
     * @param {number} entryPrice - Entry price for fallback calculation
     * @param {number} multiplier - ATR multiplier (default 1.5)
     * @returns {Promise<{distance: number, isFallback: boolean, atr?: number}>}
     */
    async getATRDistance(symbol, entryPrice, multiplier = 1.5) {
        // Try ATRService first
        if (window.ATRService?.getATR) {
            try {
                const atrData = await window.ATRService.getATR(symbol);
                if (atrData?.atr) {
                    return {
                        distance: atrData.atr * multiplier,
                        atr: atrData.atr,
                        isFallback: false
                    };
                }
            } catch (e) {
                console.warn(`[CHASE-UTILS] ATRService error for ${symbol}:`, e.message);
            }
        }

        // Try cached ATR
        if (window.ATRService?.getCachedATR) {
            const cached = window.ATRService.getCachedATR(symbol);
            if (cached?.atr) {
                return {
                    distance: cached.atr * multiplier,
                    atr: cached.atr,
                    isFallback: false
                };
            }
        }

        // Fallback: 2% of entry price
        const fallbackDistance = entryPrice * this.ATR_FALLBACK_PERCENT * multiplier;
        console.warn(`[CHASE-UTILS] No ATR for ${symbol}, using 2% fallback: ${fallbackDistance.toFixed(4)}`);
        return {
            distance: fallbackDistance,
            isFallback: true
        };
    }

    /**
     * Cancel an order
     *
     * @param {string} symbol - Asset symbol
     * @param {string} orderId - Order ID
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async cancelOrder(symbol, orderId) {
        if (!window.BrowserHyperliquidClient) {
            return { success: false, error: 'Client not available' };
        }

        try {
            const result = await window.BrowserHyperliquidClient.cancelOrder({
                orderId: orderId,
                asset: symbol
            });
            return result;
        } catch (error) {
            console.error(`[CHASE-UTILS] Cancel error:`, error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Convert direction format
     * @param {string} direction - 'LONG', 'SHORT', 'BUY', 'SELL', 'B', 'A'
     * @param {string} format - 'direction' | 'side_api' | 'side_tracker'
     * @returns {string}
     */
    convertSide(direction, format = 'direction') {
        // Normalize input
        let normalized;
        if (direction === 'LONG' || direction === 'BUY' || direction === 'B' || direction === 'buy') {
            normalized = 'LONG';
        } else {
            normalized = 'SHORT';
        }

        switch (format) {
            case 'direction':
                return normalized; // 'LONG' or 'SHORT'
            case 'side_api':
                return normalized === 'LONG' ? 'B' : 'A'; // API format
            case 'side_tracker':
                return normalized === 'LONG' ? 'BUY' : 'SELL'; // Tracker format
            case 'side_sdk':
                return normalized === 'LONG' ? 'buy' : 'sell'; // SDK format
            default:
                return normalized;
        }
    }
}

// Export singleton
if (typeof window !== 'undefined') {
    window.ChaseUtils = new ChaseUtils();
    console.log('[CHASE-UTILS] Loaded and ready');
}

export { ChaseUtils };
